import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    ArrowDataTransferHorizontalStrokeRounded,
    ArrowDataTransferVerticalStrokeRounded,
} from '@hugeicons-pro/core-stroke-rounded'
import { ChromeReaderMode, ChromeReaderModeOutlined, HowToRegRounded, Receipt } from '@mui/icons-material'
import { Box, Button, TableCell, TableRow, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetMaterialsQuery } from '@/app/services/materials'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { useGetSheetsQuery } from '@/app/services/sheets'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectOrganisationId, selectUseImperialUnits } from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import CaptionedValue from '@/common/components/CaptionedValue/CaptionedValue'
import RenderPartSize from '@/common/components/RenderPartSize/RenderPartSize'
import ThumbnailImage from '@/common/components/ThumbnailImage/ThumbnailImage'
import { getMaterialConsumptionName } from '@/common/helpers/MaterialConsumptionModes'
import { useCurrencyFormatter, useNumberFormatter, useToolBoxTreatments } from '@/common/hooks'
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags'
import { quoteItemErrors, quoteItemHasIssues, quoteItemWarnings } from '@/common/utils/quoteUtilities'
import { getSizeCaptionKey, getThicknessDisplay, hasWebThickness } from '@/common/utils/SheetUtils/SheetUtils'
import DocumentsButton from '@/features/quotes/components/QuoteAttachments/DocumentsButton'
import DocumentsDialog from '@/features/quotes/components/QuoteAttachments/DocumentsDialog'
import QuoteItemAttachmentsPanel from '@/features/quotes/components/QuoteAttachments/QuoteItemAttachmentsPanel'

import QuoteItemStatus from '../common/QuoteItemStatus'

const classes = {
    tableCell: {
        py: 1.5,
    },
    tableCellBorder: {
        borderRight: (theme) => `1px dashed ${theme.palette.text.disabled}`,
    },
}

const QuoteReviewTableRow = ({ handleOpenNotesModal, handleOpenPriceDetailsModal, quoteItem }) => {
    const { t } = useTranslation()

    const { showGrainDirection, showRenamedConsumptionModes } = useToolBoxTreatments()
    const { showQuoteAttachments } = useFeatureFlags()
    const { quoteId } = useParams()
    const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false)

    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const { data: materials, isLoading: isLoadingMaterials } = useGetMaterialsQuery({ organisationId })
    const { data: cuttingTechnologies, isLoading: isLoadingCT } = useGetCuttingTechnologiesQuery({ organisationId })
    const { data: sheets, isLoading: isLoadingSheets } = useGetSheetsQuery({ organisationId })

    const material = useMemo(() => {
        if (isLoadingMaterials) return null
        return materials[quoteItem.materialId]
    }, [quoteItem, materials, isLoadingMaterials])

    const sheet = useMemo(() => {
        if (isLoadingSheets) return null
        return sheets[quoteItem?.sheetId]
    }, [quoteItem, sheets, isLoadingSheets])

    const cuttingTechnology = useMemo(() => {
        if (isLoadingCT) return null
        return cuttingTechnologies[quoteItem?.cuttingTechnologyId]
    }, [quoteItem, cuttingTechnologies, isLoadingCT])

    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const { n: metricNumberFormatter } = useNumberFormatter({
        numberOfDecimalPlaces: 2,
    })

    const { n: imperialNumberFormatter } = useNumberFormatter({
        numberOfDecimalPlaces: 4,
    })

    const partHasIssues = quoteItemHasIssues(quoteItem)
    const partWarnings = quoteItemWarnings(quoteItem)
    const partErrors = quoteItemErrors(quoteItem)

    const isRotaryPart = 'profile' in quoteItem && Boolean(quoteItem?.profile)
    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const partHasNotes = useMemo(() => {
        const { customerNotes, privateNotes, vendorNotes } = quoteItem
        return Boolean(customerNotes) || Boolean(vendorNotes) || Boolean(privateNotes)
    }, [quoteItem])

    const units = useImperialUnits ? 'in' : 'mm'

    const getSheetSize = () => {
        const n = useImperialUnits ? imperialNumberFormatter : metricNumberFormatter

        if (sheet?.profile) {
            if (sheet?.profile === 'Circle') {
                return `${n(sheet?.diameter)} x ${n(sheet?.materialLength)}`
            } else {
                return `${n(sheet?.sheetWidth)} x ${n(sheet?.sheetHeight)} x ${n(sheet?.materialLength)}`
            }
        } else {
            return `${n(sheet?.sheetWidth)} x ${n(sheet?.sheetHeight)}`
        }
    }

    const getProcessDetails = useCallback(
        (processId) => {
            return organisationSecondaryProcesses?.find((osp) => osp?.secondaryProcessId === processId)
        },
        [organisationSecondaryProcesses]
    )

    const sortedProcesses = useMemo(() => {
        return quoteItem.secondaryProcesses
            ?.map((process) => ({
                ...process,
                index: getProcessDetails(process.secondaryProcessId)?.index,
            }))
            .sort((a, b) => (a.index < b.index ? -1 : 1))
    }, [quoteItem, getProcessDetails])

    const renderPartDetails = (part) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                }}
            >
                {!partHasIssues ? <QuoteItemStatus marginLeft={0} /> : null}
                {partHasIssues && partWarnings ? (
                    <QuoteItemStatus
                        marginLeft={0}
                        messages={partWarnings}
                        status="warning"
                    />
                ) : null}
                {partHasIssues && partErrors ? (
                    <QuoteItemStatus
                        marginLeft={0}
                        messages={partErrors}
                        status="error"
                    />
                ) : null}
                <Box>
                    <Typography
                        sx={{ whiteSpace: 'break-space' }}
                        variant="body2"
                    >
                        {part?.name}
                    </Typography>
                    {isPartFromLibrary ? (
                        <Typography variant="body2">
                            {t('ID: ')} {part?.partId}
                        </Typography>
                    ) : null}

                    {isRotaryPart ? (
                        <Typography variant="body2">
                            {t('Profile')}:{' '}
                            <Typography
                                component="span"
                                variant="strong2"
                            >
                                {t(part?.sheet?.profile)}
                            </Typography>
                        </Typography>
                    ) : null}
                    <RenderPartSize
                        part={part}
                        variant="strong2"
                    />
                </Box>
                <Box
                    sx={{ alignItems: 'center', columnGap: 2, display: 'flex', flexWrap: 'wrap', whiteSpace: 'nowrap' }}
                >
                    <Button
                        color="secondary"
                        size="small"
                        startIcon={partHasNotes ? <ChromeReaderMode /> : <ChromeReaderModeOutlined />}
                        sx={{ px: 0, width: 'fit-content' }}
                        variant="text"
                        disableRipple
                        onClick={() => handleOpenNotesModal(quoteItem)}
                    >
                        {t('Notes')}
                    </Button>
                    {showQuoteAttachments ? (
                        <DocumentsButton
                            attachments={quoteItem?.attachments}
                            onClick={() => setIsDocumentsDialogOpen(true)}
                        />
                    ) : null}
                </Box>
            </Box>
        )
    }

    const renderMaterialDetails = (part) => {
        const n = useImperialUnits ? imperialNumberFormatter : metricNumberFormatter
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Box>
                    <Typography
                        sx={{ whiteSpace: 'break-space' }}
                        variant="body2"
                    >
                        {material?.materialName}
                    </Typography>

                    <CaptionedValue caption={hasWebThickness(sheet) ? t('web / flange') : ''}>
                        <Typography
                            sx={{ whiteSpace: 'break-space' }}
                            variant="body2"
                        >
                            {`${sheet ? getThicknessDisplay(sheet, n, { thickness: part?.thickness }) : n(part?.thickness)} ${units}`}
                        </Typography>
                    </CaptionedValue>
                    <CaptionedValue
                        caption={t(
                            getSizeCaptionKey({
                                hasLength: Boolean(sheet?.profile),
                                isCircle: sheet?.profile === 'Circle',
                            })
                        )}
                    >
                        <Typography variant="body2">{`${getSheetSize()} ${units}`}</Typography>
                    </CaptionedValue>
                </Box>
                {showGrainDirection && part?.grainDirection !== 'None' ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-start',
                            width: 1,
                        }}
                    >
                        <Typography variant="body2">{t('Grain direction')}</Typography>
                        {part?.grainDirection === 'Horizontal' ? (
                            <TbxTooltip title={t('Horizontal grain direction')}>
                                <HugeiconsIcon
                                    aria-label="Horizontal grain direction"
                                    color="textSecondary"
                                    icon={ArrowDataTransferHorizontalStrokeRounded}
                                    size={21}
                                />
                            </TbxTooltip>
                        ) : (
                            <TbxTooltip title={t('Vertical grain direction')}>
                                <HugeiconsIcon
                                    aria-label="Vertical grain direction"
                                    color="textSecondary"
                                    icon={ArrowDataTransferVerticalStrokeRounded}
                                    size={21}
                                />
                            </TbxTooltip>
                        )}
                    </Box>
                ) : null}
                {part?.isCustomerSuppliedMaterial ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: 1,
                        }}
                    >
                        <Typography variant="body2">{t('Customer supplied')}</Typography>
                        <HowToRegRounded
                            color="secondary"
                            fontSize="small"
                        />
                    </Box>
                ) : null}
                <Typography variant="body2">
                    {t(
                        getMaterialConsumptionName(
                            material?.type === 'Rotary',
                            part?.materialConsumptionMode,
                            showRenamedConsumptionModes
                        )
                    )}
                </Typography>
            </Box>
        )
    }

    const renderSecondayProcesses = (part) => {
        return (
            <Box
                sx={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 0.5,
                    justifyContent: 'flex-start',
                }}
            >
                {part?.numberOfFolds > 0 ? <Typography variant="body2">{t('Folding')}</Typography> : null}
                {sortedProcesses?.map((secProc, index) => (
                    <Box
                        key={index}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                        }}
                    >
                        <Typography variant="body2">
                            {getProcessDetails(secProc?.secondaryProcessId)?.name}{' '}
                            {getProcessDetails(secProc?.secondaryProcessId)?.customPropertyName
                                ? `(${getProcessDetails(secProc?.secondaryProcessId)?.customPropertyName}: ${secProc?.customValue})`
                                : ''}
                        </Typography>
                    </Box>
                ))}
            </Box>
        )
    }

    return !isLoadingMaterials && !isLoadingSheets && !isLoadingCT ? (
        <TableRow key={quoteItem.id}>
            <TableCell
                align="center"
                sx={classes.tableCell}
            >
                <ThumbnailImage
                    uri={quoteItem.thumbnailUri}
                    wrapperWidth={1}
                />
            </TableCell>
            <TableCell
                align="left"
                sx={classes.tableCell}
            >
                {renderPartDetails(quoteItem)}
            </TableCell>

            <TableCell
                align="left"
                sx={classes.tableCell}
            >
                {cuttingTechnology?.name}
            </TableCell>

            <TableCell
                align="left"
                sx={classes.tableCell}
            >
                {renderMaterialDetails(quoteItem)}
            </TableCell>

            <TableCell
                align="left"
                sx={classes.tableCell}
            >
                {renderSecondayProcesses(quoteItem)}
            </TableCell>

            <TableCell
                align="right"
                sx={[classes.tableCell, classes.tableCellBorder]}
            >
                {quoteItem?.quantity}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                {formatCurrency(quoteItem?.materialPrice)}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                {formatCurrency(quoteItem?.processingTotalPrice)}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                {formatCurrency(quoteItem?.foldingTotalPricePerItem)}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                {formatCurrency(quoteItem?.secondaryProcessTotalPricePerItem)}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                {formatCurrency(quoteItem?.otherChargePrice)}
            </TableCell>

            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                <Typography variant="strong2">{formatCurrency(quoteItem?.itemPrice)}</Typography>
            </TableCell>
            <TableCell
                align="right"
                sx={classes.tableCell}
            >
                <Box position="relative">
                    <Typography variant="strong2">{formatCurrency(quoteItem?.linePrice)}</Typography>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                        }}
                    >
                        <Button
                            data-testid="nqp-quote-item-unit-times-details"
                            endIcon={<Receipt />}
                            size="small"
                            sx={{ px: 0, width: 'fit-content' }}
                            variant="text"
                            disableRipple
                            onClick={() => handleOpenPriceDetailsModal(quoteItem)}
                        >
                            {t('Details')}
                        </Button>
                    </Box>
                </Box>
                {showQuoteAttachments ? (
                    <DocumentsDialog
                        itemName={quoteItem?.name}
                        open={isDocumentsDialogOpen}
                        subtitle={t('Attachments for this part only')}
                        onClose={() => setIsDocumentsDialogOpen(false)}
                    >
                        <QuoteItemAttachmentsPanel
                            organisationId={organisationId}
                            quoteId={quoteId}
                            quoteItemId={quoteItem.id}
                        />
                    </DocumentsDialog>
                ) : null}
            </TableCell>
        </TableRow>
    ) : null
}
QuoteReviewTableRow.propTypes = {
    handleOpenNotesModal: PropTypes.func,
    handleOpenPriceDetailsModal: PropTypes.func,
    quoteItem: PropTypes.object,
}
export default QuoteReviewTableRow
